import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type {
  BillingAssignmentView,
  FeeAssignmentView,
  FeeAssignmentsView,
  FeeConcessionKind,
  FeePlanVersionView,
  FeePlansView,
  SupportedLocale,
} from '@stm/contracts';
import { feeConcessionKinds } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { apiRequest } from './api';
import { formatCalendarDate } from './athletes';
import {
  concessionKindKeys,
  formatBasisPoints,
  formatMoney,
  parseAmountInput,
  parsePercentInput,
} from './billing';
import { errorKeyOf, type ErrorKey } from './BillingForms';

export interface BillingFeeAssignmentsProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}/billing`. */
  base: string;
  csrfToken: string;
  account: { id: string; currency: string; active: boolean };
  /** The athletes assigned to the account; only they can be given a fee on it. */
  athletes: readonly Pick<BillingAssignmentView, 'athleteId' | 'athleteName'>[];
}

const todayInUtc = (): string => new Date().toISOString().slice(0, 10);

function planLabel(plan: FeePlanVersionView, locale: SupportedLocale): string {
  const name =
    (locale === 'hu' ? plan.nameHu : locale === 'ro' ? plan.nameRo : null) ?? plan.nameEn;
  return `${name} · ${formatMoney(plan.amountMinor, plan.currency, locale)} (${translate(locale, 'billing.fees.plan.version')} ${String(plan.versionNumber)})`;
}

function concessionText(assignment: FeeAssignmentView, locale: SupportedLocale): string | null {
  const { kind, value } = assignment.concession;
  if (kind === 'none') return null;
  const amount =
    kind === 'percent'
      ? formatBasisPoints(value, locale)
      : formatMoney(value, assignment.currency, locale);
  return `${translate(locale, concessionKindKeys[kind])}: ${amount}`;
}

/**
 * The fee assignments of one account: which athlete pays which fee plan version from when, with an
 * approved concession, a pause and an end date. An athlete has one fee at a time on an account; a
 * plan change is an end date plus a new assignment, and the fee run never bills a month twice.
 */
export function BillingFeeAssignments({
  locale,
  base,
  csrfToken,
  account,
  athletes,
}: BillingFeeAssignmentsProps): React.JSX.Element {
  const [assignments, setAssignments] = useState<FeeAssignmentView[] | null>(null);
  const [plans, setPlans] = useState<FeePlanVersionView[]>([]);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [athleteId, setAthleteId] = useState('');
  const [planVersionId, setPlanVersionId] = useState('');
  const [startsOn, setStartsOn] = useState(todayInUtc);
  const [kind, setKind] = useState<FeeConcessionKind>('none');
  const [valueText, setValueText] = useState('');
  const [reason, setReason] = useState('');
  const [pausing, setPausing] = useState<string | null>(null);
  const [pausedFrom, setPausedFrom] = useState(todayInUtc);
  const [pausedUntil, setPausedUntil] = useState('');

  const load = useCallback(async () => {
    try {
      const [fees, planList] = await Promise.all([
        apiRequest<FeeAssignmentsView>(`${base}/fee-assignments?accountId=${account.id}`),
        apiRequest<FeePlansView>(`${base}/fee-plans`),
      ]);
      setAssignments(fees.items);
      setPlans(planList.items);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [account.id, base]);

  useEffect(() => {
    void load();
  }, [load]);

  // Only the newest version of each plan in the account's currency is offered for a new assignment.
  const offeredPlans = plans.filter(
    (plan) =>
      plan.currency === account.currency &&
      !plans.some(
        (other) => other.planId === plan.planId && other.versionNumber > plan.versionNumber,
      ),
  );
  const athleteOptions = [
    ...new Map(athletes.map((athlete) => [athlete.athleteId, athlete.athleteName])),
  ];

  const concessionValue =
    kind === 'amount'
      ? parseAmountInput(valueText)
      : kind === 'percent'
        ? parsePercentInput(valueText)
        : '0';
  const concessionValid = kind === 'none' || (concessionValue !== null && reason.trim() !== '');
  const canAssign = athleteId !== '' && planVersionId !== '' && startsOn !== '' && concessionValid;

  async function assign(): Promise<void> {
    if (!canAssign || concessionValue === null) return;
    setErrorKey(null);
    try {
      await apiRequest(`${base}/fee-assignments`, {
        method: 'POST',
        csrfToken,
        body: {
          athleteId,
          planVersionId,
          accountId: account.id,
          startsOn,
          concession: {
            kind,
            value: concessionValue,
            reason: kind === 'none' ? null : reason.trim(),
          },
        },
      });
      setAthleteId('');
      setKind('none');
      setValueText('');
      setReason('');
      await load();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  async function change(
    assignment: FeeAssignmentView,
    action: 'end' | 'pause',
    body: Record<string, unknown>,
  ): Promise<void> {
    setErrorKey(null);
    try {
      await apiRequest(`${base}/fee-assignments/${assignment.id}/${action}`, {
        method: 'POST',
        csrfToken,
        body,
        ifMatchVersion: assignment.version,
      });
      setPausing(null);
      await load();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  return (
    <Box component="section" aria-labelledby="fee-assignments-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h3" id="fee-assignments-title" gutterBottom>
        {translate(locale, 'billing.fees.assign.title')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {assignments?.length === 0 && (
        <Typography>{translate(locale, 'billing.fees.assign.empty')}</Typography>
      )}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {assignments?.map((assignment) => {
          const concession = concessionText(assignment, locale);
          const open = assignment.endsOn === null;
          return (
            <Box
              component="li"
              key={assignment.id}
              sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
            >
              <Typography component="span">
                {assignment.athleteName} · {assignment.planName} ·{' '}
                {formatCalendarDate(assignment.startsOn, locale)}
                {assignment.endsOn !== null &&
                  ` ${translate(locale, 'billing.assignments.until')} ${formatCalendarDate(assignment.endsOn, locale)}`}
                {concession !== null && ` · ${concession}`}
              </Typography>
              {assignment.pausedFrom !== null && (
                <Chip
                  size="small"
                  label={`${translate(locale, 'billing.fees.assign.pausedBadge')}: ${formatCalendarDate(assignment.pausedFrom, locale)}${
                    assignment.pausedUntil === null
                      ? ''
                      : ` – ${formatCalendarDate(assignment.pausedUntil, locale)}`
                  }`}
                />
              )}
              {open && account.active && (
                <>
                  <Button
                    size="small"
                    onClick={() => {
                      void change(assignment, 'end', {});
                    }}
                  >
                    {translate(locale, 'billing.fees.assign.end')}
                  </Button>
                  {assignment.pausedFrom === null ? (
                    <Button
                      size="small"
                      onClick={() => {
                        setPausing(pausing === assignment.id ? null : assignment.id);
                      }}
                    >
                      {translate(locale, 'billing.fees.assign.pause')}
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      onClick={() => {
                        void change(assignment, 'pause', { pausedFrom: null });
                      }}
                    >
                      {translate(locale, 'billing.fees.assign.resume')}
                    </Button>
                  )}
                </>
              )}
              {pausing === assignment.id && (
                <Stack
                  component="form"
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ width: '100%' }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    void change(assignment, 'pause', {
                      pausedFrom,
                      pausedUntil: pausedUntil === '' ? null : pausedUntil,
                    });
                  }}
                >
                  <TextField
                    required
                    size="small"
                    type="date"
                    label={translate(locale, 'billing.fees.assign.pausedFrom')}
                    value={pausedFrom}
                    onChange={(event) => {
                      setPausedFrom(event.target.value);
                    }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label={translate(locale, 'billing.fees.assign.pausedUntil')}
                    value={pausedUntil}
                    onChange={(event) => {
                      setPausedUntil(event.target.value);
                    }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <Button
                    type="submit"
                    variant="outlined"
                    disabled={pausedFrom === '' || (pausedUntil !== '' && pausedUntil < pausedFrom)}
                  >
                    {translate(locale, 'billing.fees.assign.pause')}
                  </Button>
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>

      {account.active && offeredPlans.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {translate(
            locale,
            plans.length === 0
              ? 'billing.fees.assign.noPlans'
              : 'billing.fees.assign.noPlansInCurrency',
          )}
        </Typography>
      )}
      {account.active && offeredPlans.length > 0 && (
        <Stack
          component="form"
          spacing={2}
          sx={{ mt: 2, maxWidth: 560 }}
          onSubmit={(event) => {
            event.preventDefault();
            void assign();
          }}
        >
          <TextField
            select
            required
            size="small"
            label={translate(locale, 'billing.fees.assign.athlete')}
            value={athleteId}
            onChange={(event) => {
              setAthleteId(event.target.value);
            }}
          >
            {athleteOptions.map(([id, name]) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            required
            size="small"
            label={translate(locale, 'billing.fees.assign.plan')}
            value={planVersionId}
            onChange={(event) => {
              setPlanVersionId(event.target.value);
            }}
          >
            {offeredPlans.map((plan) => (
              <MenuItem key={plan.id} value={plan.id}>
                {planLabel(plan, locale)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            size="small"
            type="date"
            label={translate(locale, 'billing.fees.assign.startsOn')}
            value={startsOn}
            onChange={(event) => {
              setStartsOn(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            select
            size="small"
            label={translate(locale, 'billing.fees.concession.label')}
            value={kind}
            onChange={(event) => {
              setKind(event.target.value as FeeConcessionKind);
              setValueText('');
            }}
          >
            {feeConcessionKinds.map((option) => (
              <MenuItem key={option} value={option}>
                {translate(locale, concessionKindKeys[option])}
              </MenuItem>
            ))}
          </TextField>
          {kind !== 'none' && (
            <>
              <TextField
                required
                size="small"
                label={translate(
                  locale,
                  kind === 'amount'
                    ? 'billing.fees.concession.amountValue'
                    : 'billing.fees.concession.percentValue',
                )}
                value={valueText}
                error={valueText.trim() !== '' && concessionValue === null}
                onChange={(event) => {
                  setValueText(event.target.value);
                }}
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              />
              <TextField
                required
                size="small"
                label={translate(locale, 'billing.fees.concession.reason')}
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
                slotProps={{ htmlInput: { maxLength: 500 } }}
              />
              {!concessionValid && (valueText !== '' || reason !== '') && (
                <Typography variant="body2" color="text.secondary">
                  {translate(locale, 'billing.fees.concession.invalid')}
                </Typography>
              )}
            </>
          )}
          <Box>
            <Button type="submit" variant="contained" disabled={!canAssign}>
              {translate(locale, 'billing.fees.assign.submit')}
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
